import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import industryData from '../data/industryData.json';

const Sidebar = ({
  activeView,
  onViewChange,
  sidebarClosed,
  onToggleSidebar,
  prospectFilterExpanded,
  onToggleProspectFilter,
  filters,
  setFilters,
  userId,
  onSearch,
  searchType,
  currentUser,
  onLogout
}) => {
  const FULL_LOGO_SRC = '/image/logo.png';
  const SIDE_LOGO_SRC = '/image/side_logo.png';

  // Predefined filter options (to avoid using Lusha API quota)
  const PREDEFINED_DEPARTMENTS = [
    'Business Development',
    'Consulting',
    'Customer Service',
    'Engineering & Technical',
    'Finance',
    'General Management',
    'Health Care & Medical',
    'Human Resources',
    'Information Technology',
    'Legal',
    'Marketing',
    'Operations',
    'Other',
    'Product',
    'Research & Analytics',
    'Sales'
  ];

  // Seniority mapping: display text -> Lusha ID (must match ProspectFinderView.js)
  const SENIORITY_MAPPING = {
    'Founder': 10,
    'C-suite': 9,
    'Vice President': 8,
    'partner': 7,
    'Director': 6,
    'Manager': 5,
    'Senior': 4,
    'Entry': 3,
    'Intern': 2,
    'Other': 1
  };

  const PREDEFINED_SENIORITY = Object.keys(SENIORITY_MAPPING);

  // Predefined company sizes (exact format for Lusha API)
  // Predefined company sizes (exact format for Lusha API)
  const PREDEFINED_SIZES = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5001-10000',
    '10001-100000',
    '100001+'
  ];

    // Predefined company revenue (exact format for Lusha API)
    const PREDEFINED_REVENUES = [
      '1 - 1M',
      '1M - 5M',
      '5M - 10M',
      '10M - 50M',
      '50M - 100M',
      '100M - 250M',
      '250M - 500M',
      '500M - 1B',
      '1B - 10B',
      '500M - 10B',
      '10B - 100B',
      '100B+'
    ];

  // State for filter options from Lusha API
  const [filterOptions, setFilterOptions] = useState({
    departments: PREDEFINED_DEPARTMENTS,
    seniority: PREDEFINED_SENIORITY,
    sizes: PREDEFINED_SIZES,
    industries: [],
    revenues: PREDEFINED_REVENUES,
    countries: []
  });

  // State for expanded industries
  const [expandedIndustries, setExpandedIndustries] = useState({});

  const [loadingFilters, setLoadingFilters] = useState({
    departments: false,
    seniority: false,
    industries: false,
    sizes: false,
    revenues: false
  });

  // Helper to extract string values from API responses
  const extractValues = (data) => {
    if (!Array.isArray(data)) return [];
    return data.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        return item.name || item.label || item.value || item.title || JSON.stringify(item);
      }
      return String(item);
    });
  };

  // Departments, Seniority, and Company Sizes are now predefined - no need to fetch

  // State for showing industry tree
  const [showIndustryTree, setShowIndustryTree] = useState(false);
  const [industrySearch, setIndustrySearch] = useState('');

  // Industry tree component with checkboxes
  const IndustryTree = () => {
    // Filter logic
    const filteredData = industryData.map(item => {
      // If search is empty, return everything
      if (!industrySearch.trim()) return item;

      const searchLower = industrySearch.toLowerCase();
      const mainMatch = item.main_industry.toLowerCase().includes(searchLower);
      
      // Check sub industries
      const matchingSubs = (item.sub_industries || []).filter(sub => 
        sub.value.toLowerCase().includes(searchLower)
      );

      if (mainMatch) {
        // If main matches, keep all subs (or maybe just show the main?) -> Let's keep structure full for main match
        return item;
      } else if (matchingSubs.length > 0) {
        // If only subs match, return main with filtered subs, and Auto-Assign Expanded
        return { ...item, sub_industries: matchingSubs, forceExpand: true };
      }
      
      return null;
    }).filter(Boolean);

    const toggleMainIndustry = (mainIndustryName) => {
      setExpandedIndustries(prev => ({
        ...prev,
        [mainIndustryName]: !prev[mainIndustryName]
      }));
    };

    const handleMainIndustryCheck = (mainIndustryName, checked) => {
      const currentIndustries = filters.industries || [];
      const mainIndustryLabel = `${mainIndustryName} (Main)`;
      
      if (checked) {
        if (!currentIndustries.includes(mainIndustryLabel)) {
          setFilters({
            ...filters,
            industries: [...currentIndustries, mainIndustryLabel]
          });
        }
      } else {
        setFilters({
          ...filters,
          industries: currentIndustries.filter(ind => ind !== mainIndustryLabel)
        });
      }
    };

    const handleSubIndustryCheck = (subIndustryName, checked) => {
      const currentIndustries = filters.industries || [];
      const subIndustryLabel = `${subIndustryName} (Sub)`;
      
      if (checked) {
        if (!currentIndustries.includes(subIndustryLabel)) {
          setFilters({
            ...filters,
            industries: [...currentIndustries, subIndustryLabel]
          });
        }
      } else {
        setFilters({
          ...filters,
          industries: currentIndustries.filter(ind => ind !== subIndustryLabel)
        });
      }
    };

    const isMainIndustryChecked = (mainIndustryName) => {
      return (filters.industries || []).includes(`${mainIndustryName} (Main)`);
    };

    const isSubIndustryChecked = (subIndustryName) => {
      return (filters.industries || []).includes(`${subIndustryName} (Sub)`);
    };

    return (
      <div className="industry-tree" 
        onMouseDown={(e) => e.preventDefault()} // Prevent blur on input
        style={{
          maxHeight: '260px',
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          padding: '8px 0',
          fontSize: '14px',
          
          // Absolute positioning to match ChipInput suggestions
          position: 'absolute',
          top: '100%',
          left: -1, 
          right: -1,
          zIndex: 1000,
          marginTop: '-1px', // Overlap border slightly
          borderTop: 'none'
      }}>
        {filteredData.length > 0 ? filteredData.map((industry, index) => {
           const isExpanded = industry.forceExpand || expandedIndustries[industry.main_industry];
           
           return (
          <div key={index} style={{ marginBottom: '0', transition: 'background 0.2s' }}
            onMouseOver={e => e.currentTarget.style.background = '#f5f8fa'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px' }}>
              <button
                onClick={(e) => { e.stopPropagation(); toggleMainIndustry(industry.main_industry); }}
                // Prevent default on mouse down to avoid stealing focus from input
                onMouseDown={(e) => e.preventDefault()}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  fontSize: '10px',
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#888',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                  marginRight: '4px',
                  borderRadius: '2px'
                }}
                title={isExpanded ? 'Collapse' : 'Expand'}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
              </button>
              <input
                type="checkbox"
                checked={isMainIndustryChecked(industry.main_industry)}
                onChange={(e) => handleMainIndustryCheck(industry.main_industry, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.preventDefault()}
                style={{ margin: 0, marginRight: '-22px', width: '13px', height: '13px', cursor: 'pointer', flexShrink: 0 }}
              />
              <label style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 400, flex: 1, lineHeight: '1.2' }} onClick={() => toggleMainIndustry(industry.main_industry)}>
                {industry.main_industry}
              </label>
            </div>
            {isExpanded && industry.sub_industries && (
              <div style={{ paddingLeft: '0', marginTop: '0' }}>
                {industry.sub_industries.map((subInd, subIndex) => (
                  <div key={subIndex} 
                    style={{ display: 'flex', alignItems: 'center', padding: '4px 8px 4px 24px' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f5f8fa'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <input
                      type="checkbox"
                      checked={isMainIndustryChecked(industry.main_industry) || isSubIndustryChecked(subInd.value)}
                      onChange={(e) => handleSubIndustryCheck(subInd.value, e.target.checked)}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{ margin: 0, marginRight: '5px', width: '13px', height: '13px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <label style={{ cursor: 'pointer', fontSize: '13px', flex: 1, lineHeight: '1.2' }}>
                      {subInd.value}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}) : (
          <div style={{ padding: '8px 12px', color: '#999', fontSize: '13px', fontStyle: 'italic' }}>
             No industries found
          </div>
        )}
      </div>
    );
  };

  // Load industries from imported JSON data (no longer needed for flat list, but keep for reference)
  useEffect(() => {
    try {
      console.log('Loading industries from imported data');
      
      // Extract both main industries and sub-industries with subtle labels
      const allIndustries = [];
      
      industryData.forEach(item => {
        // Add main industry with label
        allIndustries.push(`${item.main_industry} (Main)`);
        
        // Add sub-industries with label
        if (item.sub_industries && Array.isArray(item.sub_industries)) {
          item.sub_industries.forEach(subInd => {
            if (subInd.value) {
              allIndustries.push(`${subInd.value} (Sub)`);
            }
          });
        }
      });
      
      console.log('Loaded industries (main + sub):', allIndustries);
      setFilterOptions(prev => ({ ...prev, industries: allIndustries }));
    } catch (error) {
      console.error('Error loading industries:', error);
    }
  }, []);

  // Fetch countries from Lusha API
  const fetchCountries = async () => {
    if (filterOptions.countries.length > 0) return; // Already loaded
    
    try {
      const response = await axios.get('/api/lusha/filters/contacts/countries');
      const values = extractValues(response.data);
      setFilterOptions(prev => ({ ...prev, countries: values }));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleSearch = () => {
    // Change view to prospect-finder when search is clicked
    onViewChange('prospect-finder');
    
    if (onSearch) {
      onSearch(filters);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      jobTitles: [],
      managementLevels: [],
      departments: [],
      locations: [],
      companyNames: [],
      industries: [],
      companySizes: [],
      companyRevenues: [],
      skills: []
    });
  };

  const addChip = (filterKey, value) => {
    if (!value.trim()) return;
    const currentValues = filters[filterKey] || [];
    if (!currentValues.includes(value.trim())) {
      setFilters({
        ...filters,
        [filterKey]: [...currentValues, value.trim()]
      });
    }
  };

  const removeChip = (filterKey, value) => {
    setFilters({
      ...filters,
      [filterKey]: filters[filterKey].filter(item => item !== value)
    });
  };

  const ChipInput = ({ filterKey, placeholder, datalistOptions = null, onFocus = null }) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);

    // Filter suggestions based on user input
    useEffect(() => {
      console.log('ChipInput - datalistOptions:', datalistOptions);
      console.log('ChipInput - inputValue:', inputValue);
      
      if (datalistOptions && datalistOptions.length > 0) {
        if (inputValue.trim()) {
          // Filter based on input
          const filtered = datalistOptions.filter(option =>
            option.toLowerCase().includes(inputValue.toLowerCase())
          );
          console.log('Filtered suggestions:', filtered);
          setFilteredSuggestions(filtered);
        } else {
          // Show all options when input is empty
          setFilteredSuggestions(datalistOptions);
        }
      } else {
        setFilteredSuggestions([]);
      }
    }, [inputValue, datalistOptions]);

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        if (filteredSuggestions.length > 0) {
          // Use first suggestion if available
          addChip(filterKey, filteredSuggestions[0]);
        } else {
          addChip(filterKey, inputValue.replace(',', ''));
        }
        setInputValue('');
        setShowSuggestions(false);
      }
    };

    const handleBlur = () => {
      // Delay to allow click on suggestion
      setTimeout(() => {
        if (inputValue.trim() && filteredSuggestions.length > 0) {
          addChip(filterKey, filteredSuggestions[0]);
          setInputValue('');
        } else if (inputValue.trim()) {
          addChip(filterKey, inputValue);
          setInputValue('');
        }
        setShowSuggestions(false);
      }, 200);
    };

    const handleFocus = () => {
      if (onFocus) {
        onFocus();
      }
      if (datalistOptions && datalistOptions.length > 0) {
        setShowSuggestions(true);
      }
    };

    const handleSuggestionClick = (suggestion) => {
      addChip(filterKey, suggestion);
      setInputValue('');
      setShowSuggestions(false);
    };

    return (
      <div className="chip-container" style={{ position: 'relative' }}>
        {(filters[filterKey] || []).map((chip, index) => {
          // Determine chip color class based on industry type
          const isIndustry = filterKey === 'industries';
          const isMain = isIndustry && chip.includes('(Main)');
          const isSub = isIndustry && chip.includes('(Sub)');
          const chipClass = isMain ? 'chip industry-main' : isSub ? 'chip industry-sub' : 'chip';
          const displayText = chip.replace(/\s*\((Main|Sub)\)/, '');
          
          return (
            <div key={index} className={chipClass}>
              {displayText}
              <span className="chip-delete" onClick={() => removeChip(filterKey, chip)}>
                <i className="fas fa-times"></i>
              </span>
            </div>
          );
        })}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
        />
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="autocomplete-suggestions" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredSuggestions.map((suggestion, index) => {
              // Check if suggestion has (Main) or (Sub) label
              const hasLabel = suggestion.includes('(Main)') || suggestion.includes('(Sub)');
              const mainText = hasLabel ? suggestion.replace(/\s*\((Main|Sub)\)/, '') : suggestion;
              const labelText = suggestion.includes('(Main)') ? 'Main' : suggestion.includes('(Sub)') ? 'Sub' : null;
              const labelClass = labelText ? labelText.toLowerCase() : '';
              
              return (
                <div
                  key={index}
                  className="suggestion-item"
                  onMouseDown={(e) => e.preventDefault()} // Prevent blur
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <span>{mainText}</span>
                  {labelText && <span className={`suggestion-label ${labelClass}`}>{labelText}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Add state for expanded/collapsed for each menu
  const [prospectListExpanded, setProspectListExpanded] = useState(false);
  const [marketAnalysisExpanded, setMarketAnalysisExpanded] = useState(false);

  return (
    <div className={`sidebar ${sidebarClosed ? 'closed' : ''}`}>
      <div className="logo">
        <img
          src={sidebarClosed ? SIDE_LOGO_SRC : FULL_LOGO_SRC}
          alt="Company Logo"
        />
      </div>

      <div className="prospect-finder-wrapper">
        <div
          className={`menu-item prospect-finder-menu ${prospectFilterExpanded ? 'expanded' : ''} ${activeView === 'prospect-finder' ? 'active' : ''}`}
          onClick={() => {
            onViewChange('prospect-finder');
            onToggleProspectFilter();
          }}
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <span className="icon"><i className="fas fa-users"></i></span>
          <span>Prospect Finder</span>
          <span className="chevron"><i className="fas fa-chevron-down"></i></span>
        </div>

        <div
          className="filter-group-content"
          style={{
            maxHeight: prospectFilterExpanded && !sidebarClosed ? '500px' : '0'
          }}
        >
          {/* Prospect-specific filters - only show when searchType is 'prospects' */}
          {searchType === 'prospects' && (
            <>
              <div className="filter-item">
                <span className="icon"><i className="fas fa-user-tie"></i></span>
                <div className="filter-item-content">
                  <label>Job title</label>
                  <ChipInput
                    filterKey="jobTitles"
                    placeholder="Enter one or more job titles"
                  />
                </div>
              </div>

              <div className="filter-item">
                <span className="icon"><i className="fas fa-sitemap"></i></span>
                <div className="filter-item-content">
                  <label>Management Level (Seniority)</label>
                  <ChipInput
                    filterKey="managementLevels"
                    placeholder="Select management level"
                    datalistOptions={filterOptions.seniority}
                  />
                </div>
              </div>

              <div className="filter-item">
                <span className="icon"><i className="fas fa-object-group"></i></span>
                <div className="filter-item-content">
                  <label>Department</label>
                  <ChipInput
                    filterKey="departments"
                    placeholder="Select department"
                    datalistOptions={filterOptions.departments}
                  />
                </div>
              </div>
            </>
          )}

          {/* Common filters for both prospects and companies */}
          <div className="filter-item">
            <span className="icon"><i className="fas fa-map-marker-alt"></i></span>
            <div className="filter-item-content">
              <label>Location</label>
              <ChipInput
                filterKey="locations"
                placeholder="Select prospect's location"
              />
            </div>
          </div>

          <div className="filter-item">
            <span className="icon"><i className="fas fa-building"></i></span>
            <div className="filter-item-content">
              <label>Company Name</label>
              <ChipInput
                filterKey="companyNames"
                placeholder="Enter company names"
              />
            </div>
          </div>

          <div className="filter-item">
            <span className="icon"><i className="fas fa-industry"></i></span>
            <div className="filter-item-content">
              <label>Industry</label>
              <div className="industry-filter-container">
                <div className="chip-container" style={{ position: 'relative' }}>
                  {(filters.industries || []).map((chip, index) => {
                    const isMain = chip.includes('(Main)');
                    const isSub = chip.includes('(Sub)');
                    const chipClass = isMain ? 'chip industry-main' : isSub ? 'chip industry-sub' : 'chip';
                    const displayText = chip.replace(/\s*\((Main|Sub)\)/, '');
                    
                    return (
                      <div key={index} className={chipClass}>
                        {displayText}
                        <span className="chip-delete" onClick={() => removeChip('industries', chip)}>
                          <i className="fas fa-times"></i>
                        </span>
                      </div>
                    );
                  })}
                  
                  <input
                    type="text"
                    placeholder="Select industry"
                    value={industrySearch}
                    onChange={(e) => {
                       setIndustrySearch(e.target.value);
                       if (!showIndustryTree) setShowIndustryTree(true);
                    }}
                    onFocus={() => setShowIndustryTree(true)}
                    onBlur={() => {
                      setTimeout(() => setShowIndustryTree(false), 200);
                    }}
                    style={{ minWidth: '100px' }}
                  />
                  
                  {showIndustryTree && <IndustryTree />}
                </div>
              </div>
            </div>
          </div>

          <div className="filter-item">
            <span className="icon"><i className="fas fa-user-friends"></i></span>
            <div className="filter-item-content">
              <label>Size</label>
              <ChipInput
                filterKey="companySizes"
                placeholder="Select sizes"
                datalistOptions={filterOptions.sizes}
              />
            </div>
          </div>

          <div className="filter-item">
            <span className="icon"><i className="fas fa-dollar-sign"></i></span>
            <div className="filter-item-content">
              <label>Revenue</label>
              <ChipInput
                filterKey="companyRevenues"
                placeholder="Select revenues"
                datalistOptions={filterOptions.revenues}
              />
            </div>
          </div>

          <div className="filter-buttons">
            <button className="search-btn" onClick={handleSearch}>
              <i className="fas fa-search"></i> Search
            </button>
            <button className="clear-btn" onClick={handleClearFilters}>
              <i className="fas fa-times"></i> Clear Filter
            </button>
          </div>
        </div>
      </div>

      <div className="bottom-links">
        <div
          className={`menu-item ${activeView === 'prospect-list' ? 'active' : ''}`}
          onClick={() => {
            onViewChange('prospect-list');
            setProspectListExpanded((prev) => !prev);
          }}
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <span className="icon"><i className="fas fa-list-ul"></i></span>
          <span>Your Prospect list</span>
          <span className="arrow">
            <i className={`fas fa-chevron-${activeView === 'prospect-list' ? 'down' : 'right'}`}></i>
          </span>
        </div>
        <div
          className={`menu-item market-analysis-menu ${activeView === 'market-analysis' ? 'active' : ''}`}
          onClick={() => {
            onViewChange('market-analysis');
            setMarketAnalysisExpanded((prev) => !prev);
          }}
          style={{ fontSize: '14px', fontWeight: 500 }}
        >
          <span className="icon"><i className="fas fa-chart-bar"></i></span>
          <span>Market Analysis</span>
          <span className="arrow">
            <i className={`fas fa-chevron-${activeView === 'market-analysis' ? 'down' : 'right'}`}></i>
          </span>
        </div>
      </div>

      {/* Sticky user info and logout at bottom when sidebar open */}
      {!sidebarClosed && currentUser && (
        <div
          className="user-info"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            borderTop: '1px solid #e0e0e0',
            background: '#fff',
            padding: '10px 0'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-user-circle" style={{ fontSize: '20px', color: '#667eea' }}></i>
              <span style={{ fontSize: '13px', fontWeight: '500', color: '#333' }}>{currentUser.username}</span>
            </div>
            <button
              onClick={onLogout}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                color: '#999',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Logout"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}

      {/* Sticky logout icon for closed sidebar */}
      {sidebarClosed && currentUser && (
        <button
          onClick={onLogout}
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '12px 0',
            color: '#999',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            zIndex: 10
          }}
          title="Logout"
        >
          <i className="fas fa-sign-out-alt"></i>
        </button>
      )}

      <button className="toggle-btn" onClick={onToggleSidebar}>
        <i className={`fas fa-chevron-${sidebarClosed ? 'right' : 'left'}`}></i>
      </button>
    </div>
  );
};

export default Sidebar;
